import { Injectable } from '@angular/core';
import { EntityManager } from '@wephone-core/service/entity_manager';
import { WsRpcService } from '@wephone-core/service/wsrpc_service';
import { MyCallCenterStatus } from '@wephone-core/model/entity/my_callcenter_status';
import { DidRepository } from '@wephone-core/model/repository/did';
import { AgentRepository } from '@wephone-core/model/repository/agent';
import { CallQueueRepository } from '@wephone-core/model/repository/callqueue';
import { CallQueueSessionRepository } from '@wephone-core/model/repository/callqueuesession';
import { LoggerService, MessageService, localNow, parseDateTime } from '@wephone-utils';
import { HttpEngine } from '@wephone-core/service/http_engine';
import { DateTime } from 'luxon';
import { PhoneService } from '@wephone-app-phone/services';
import { CallQueueEntity } from '@wephone-core/model/entity/callqueue';
import { MyCallCenterStatusRepository } from '@wephone-core/model/repository/my_callcenter_status';
import { CallQueueSessionEntity } from '@wephone-core/model/entity/callqueuesession';

export enum CallState {
  OnHold = 'on_hold',
  CallProgressing = 'call_progressing',
  InCall = 'in_call',
  Paused = 'paused',
  Idle = 'idle',
  Disconnected = 'disconnected',
}

@Injectable()
export class AgentPanelService {
  private static _instance: AgentPanelService;

  public notifications = {
    AGENTPANEL_DATA_UPDATE: 'agentpanel:data_update',
    AGENTPANEL_CALL_ENDS: 'agentpanel:call_ends',
  };

  private readonly URL_CONNECT_CALLCENTER = '/callcenter/agent/connect';

  // private readonly URL_AGENT_CHANGE_PAUSE_REASON = '/callcenter/agent/change_pause_reason';
  // private readonly URL_ACTIVE_CALL_INFO = '/callcenter/agent/get_active_call_info';
  private readonly URL_OUTCALL_CAMPAIGN_RESCHEDULE = '/callcenter/outcallcampaign/schedule_job';
  private readonly URL_OUTCALL_CAMPAIGN_TERMINATE = '/callcenter/outcallcampaign/terminate_job';
  private readonly URL_OUTCALL_CAMPAIGN_ITEM_DETAIL = '/callcenter/outcallcampaign_item/detail';

  private agent_panel_state: string;
  connection_info;
  session_info;
  private last_session_info;
  private cached_session_data;
  private cached_session_keyid;
  private interval: any;
  public need_update_outcall_info: boolean;

  constructor(
    public messageService: MessageService,
    public wsRpcService: WsRpcService,
    private readonly phoneService: PhoneService,
    private readonly loggerService: LoggerService,
  ) {

    if (AgentPanelService._instance) {
      throw new Error('Error: Instantiation failed: Use AgentPanelService.getInstance() instead of new.');
    }
    AgentPanelService._instance = this;

    this.connection_info = {};
    this.session_info = {};
    this.need_update_outcall_info = true;
  }

  static reset(): void {
    AgentPanelService._instance = undefined;
  }

  public static getInstance(): AgentPanelService {
    return AgentPanelService._instance;
  }

  public agent_panel_get_state(): string {
    return this.agent_panel_state;
  }

  public enableAutoReload(n = 5000): void {
    // this.loggerService.log('enableAutoReload');
    if (!this.interval) {
      this.interval = setInterval(async () => {
        await this.initActiveCallInfo();
      }, n);
    }
  }

  ccConnected(): boolean {
    const status = EntityManager.getRepository<MyCallCenterStatusRepository>(
      'MyCallCenterStatusRepository'
    ).getMyStatus();

    return status && status.is_online_cc;
  }

  private notifyBOPages(): void {
    const boIframe: any = parent.document.getElementById('bo_iframe');
    if (!boIframe || boIframe.src === '') {
      return;
    }

    let sendData: any = null;
    const boOrigin = '*';
    const activeCall = this.phoneService.getActiveCall();

    if (!activeCall) {
      sendData = {
        state: {
          has_call: false,
        },
      };
    } else if (activeCall.queue && activeCall.queue.bo_type === CallQueueEntity.BO_TYPE_URL && activeCall.queue.bo_url) {
      const sessionType = activeCall.isInbound() ? 'incoming' : 'outgoing';
      const now = localNow();
      const duration = activeCall.answerTime ? Math.abs(now.diff(activeCall.answerTime, 'seconds').seconds) : undefined;
      sendData = {
        state: {
          has_call: true,
          is_onhold: activeCall.isOnhold(),
          in_call: !!(activeCall.startTime && activeCall.answerTime),
        },
        remote_number: activeCall.remoteNumber,
        local_number: activeCall.localNumber,
        session_type: sessionType,
        duration
      };
    }

    if (sendData) {
      try {
        parent.postMessage({
          protocol: 'wephone-bo-push',
          version: '1.0',
          message: 'session.update',
          data: sendData,
        }, boOrigin);
      } catch (e) {
        this.loggerService.error('Error when trying to postMessage', e);
      }
    }
  }

  private async initActiveCallInfo(): Promise<void> {
    if (!EntityManager.getInstance().hasPushAPI()) {
      return;
    }

    const callqueue_session = CallQueueSessionRepository.getInstance<CallQueueSessionRepository>().getActiveUserSession();
    this.session_info = {
      state: {
        in_call: false,
        has_call: false
      },
      qualification: {
        is_visible: false,
        data: undefined
      },
      callqueue: {}
    };

    this.connection_info = {
      is_connected: false,
      is_paused: false,
      is_aftercall_pause: false,
      pause_data: {},
      agent: undefined
    };

    // update session info
    if (callqueue_session) {
      await this._updateSessionInfo(callqueue_session);
    }
    // update agent info, connection info
    const status = this.getMyCallcenterStatus();
    if (status) {
      this._updateConnectionInfo(status);
    }

    // update panel state
    if (!this.connection_info.is_connected) {
      this.agent_panel_state = CallState.Disconnected;
    } else if (this.connection_info.is_paused) {
      this.agent_panel_state = CallState.Paused;
    } else if (this.session_info.state.has_call) {
      this.agent_panel_state = CallState.CallProgressing;
      if (this.session_info.state.in_call) {
        this.agent_panel_state = CallState.InCall;
      }
      if (this.session_info.state.is_onhold) {
        this.agent_panel_state = CallState.OnHold;
      }
    } else {
      this.agent_panel_state = CallState.Idle;
    }

    this.messageService.broadcast(this.notifications.AGENTPANEL_DATA_UPDATE);
    try {
      this.notifyBOPages();
    } catch (e) {
      this.loggerService.error('Cannot send notification to BO', e);
    }

    if (
      this.last_session_info &&
      this.last_session_info.state.in_call &&
      (!this.session_info || !this.session_info.state.in_call)
    ) {
      this.last_session_info = undefined;
      this.messageService.broadcast(this.notifications.AGENTPANEL_CALL_ENDS);
    }

    // this.last_session_info = angular.copy(this.session_info);
    this.last_session_info = { ...this.session_info };
  }

  // tslint:disable-next-line:cyclomatic-complexity
  private _updateSessionInfo = async (callqueue_session: CallQueueSessionEntity) => {
    // session_keyid
    this.session_info.session_keyid = callqueue_session.id;
    this.session_info.remote_number = callqueue_session.remote_number;
    this.session_info.local_number = callqueue_session.local_number;

    // calling_number, called_number
    // session_type
    if (callqueue_session.is_outgoing) {
      this.session_info.calling_number = callqueue_session.local_number;
      this.session_info.called_number = callqueue_session.remote_number;
      this.session_info.session_type = 'outgoing';
    } else {
      this.session_info.calling_number = callqueue_session.remote_number;
      this.session_info.called_number = callqueue_session.local_number;
      this.session_info.session_type = 'incoming';
    }

    // Transfer out data
    this.session_info.transfer_data = (callqueue_session.transfer_out && callqueue_session.transfer_out_data) || {};
    if (typeof this.session_info.transfer_data === 'string') {
      this.session_info.transfer_data = JSON.parse(this.session_info.transfer_data);
    }

    this.session_info.transfer_out = callqueue_session.transfer_out;
    this.session_info.transfer_out_data = callqueue_session.transfer_out_data;

    // welcome_msg
    const did = await DidRepository.getInstance<DidRepository>().getObjectByNumber(this.session_info.called_number);
    this.session_info.welcome_msg = did ? did.welcome_message : '';
    this.session_info.transfer_reason = callqueue_session.transfer_in_reason;

    // transfer type & agent_id
    if (callqueue_session.transfer_in_agent_id) {
      const agent = AgentRepository.getInstance<AgentRepository>().getObjectById(
        callqueue_session.transfer_in_agent_id
      );
      this.session_info.transfer_in_agent_name = agent ? agent.name : undefined;
      this.session_info.transfer_in_type = callqueue_session.transfer_in_type;
    }

    // start date
    this.session_info.start_dt = callqueue_session.start_dt;
    this.session_info.bridged_dt = callqueue_session.bridged_dt;
    if (this.session_info.bridged_dt && parseDateTime(this.session_info.bridged_dt)) {
      this.session_info.duration = Math.abs(localNow().diff(parseDateTime(this.session_info.bridged_dt)).seconds);
    }

    // calling_context
    this.session_info.calling_context = did ? did.name : '';

    // is_onhold
    this.session_info.state.is_onhold = callqueue_session.is_onhold && !callqueue_session.terminated;

    this.session_info.state.in_call =
      callqueue_session.start_dt &&
      !callqueue_session.stop_dt &&
      !callqueue_session.terminated &&
      callqueue_session.bridged_dt;
    this.session_info.state.has_call = callqueue_session.start_dt && !callqueue_session.terminated ? true : false;

    // update hold_info
    if (callqueue_session.is_onhold) {
      this.session_info.hold_info = {
        hold_since: callqueue_session.last_onhold_dt
      };
    }

    if (callqueue_session.id !== this.cached_session_keyid) {
      this.cached_session_keyid = callqueue_session.id;
      this.cached_session_data = {
        outcall_info: {
          campaign_id: callqueue_session.outcall_campaign_id,
          job_id: callqueue_session.outcall_job_id,
          scheduled_dt: undefined,
          finished: false
        }
      };
      if (callqueue_session.outcall_campaign_id && callqueue_session.outcall_job_id) {
        this.get_outcall_campaign_item_detail(callqueue_session.outcall_job_id).then(
          (response: any) => {
            // Store outcall campaign item in cached session data
            const outcall_campaign_item = response && response.item || null;
            if (!outcall_campaign_item) {
              this.loggerService.error('Outcall campaign item not found', callqueue_session.outcall_job_id);
              return;
            }

            let scheduled_dt = parseDateTime(outcall_campaign_item.scheduled_dt);
            if (outcall_campaign_item.hour_start && scheduled_dt) {
              const schedule_time = outcall_campaign_item.hour_start;
              const time_parser = schedule_time.split(':');
              scheduled_dt = scheduled_dt.set({ hour: parseInt(time_parser[0]), minute: parseInt(time_parser[1]) });
            }
            this.cached_session_data.outcall_info.scheduled_dt = scheduled_dt && scheduled_dt.toJSDate() || '';
            this.cached_session_data.outcall_info.finished = outcall_campaign_item.finished;
          },
          (response: any) => {
            this.loggerService.error('get get_outcall_campaign_item_detail', response);
          }
        );
      }

      if (callqueue_session.call_queue_keyid) {
        const callqueue = CallQueueRepository.getInstance().getObjectById(callqueue_session.call_queue_keyid);
        if (callqueue) {
          // Store callqueue in cached session data
          this.cached_session_data.callqueue = callqueue;
        } else {
          this.loggerService.error('Call queue not found', callqueue_session.call_queue_keyid);
        }
      }
    }

    Object.assign(this.session_info, this.cached_session_data);

    // update device_info
    this.session_info.device_info = {
      audio_status: callqueue_session.audio_status
    };
  }

  private _updateConnectionInfo = (status: MyCallCenterStatus) => {
    // connected state, not in used???
    this.connection_info.is_connected = status.is_connected;

    // pause
    this.connection_info.is_paused = status.pause_event_id ? true : false;
    this.connection_info.pause_data.pause_since = status.pause_event_id ? status.pause_since : undefined;
    this.connection_info.pause_data.pause_reason = status.pause_reason;
    // this.connection_info.pause_data.pause_event_id = agent.pause_event_id;

    this.connection_info.agent_id = status.id;
    this.connection_info.inout_state = status.inout_state;
  }

  transfer(session_id, assisted_transfer, operator_id, transfer_reason): Promise<any> {
    // this.loggerService.log('transfer', session_id, operator_id, transfer_reason);
    const params = {
      operator_id,
      transfer_reason,
      assisted_transfer
    };

    return this._send_session_command('transfer_call', session_id, params);
  }

  transfer_to_application(session_id, app_name, app_params): Promise<string> {
    // this.loggerService.log('transfer_to_application', session_id, app_name, app_params);
    const params = {
      app_name,
      params: app_params
    };

    return this._send_session_command('transfer_call_to_app', session_id, params);
  }

  transfer_phone(session_id, assisted_transfer, phone_number, transfer_reason): Promise<string> {
    // this.loggerService.log('transfer_phone', session_id, phone_number, transfer_reason);
    const params = {
      phone_number,
      transfer_reason,
      assisted_transfer
    };

    return this._send_session_command('transfer_call_to_number', session_id, params);
  }

  confirm_transfer(session_id): Promise<string> {
    return this._send_session_command('confirm_transfer', session_id);
  }

  cancel_transfer(session_id): Promise<string> {
    return this._send_session_command('cancel_transfer', session_id);
  }

  outcallJobTerminate(campaign_id, job_id): Promise<any> {
    // this.loggerService.log('outcall_job_terminate', campaign_id, job_id);
    return HttpEngine.getInstance().apiPostV2(this.URL_OUTCALL_CAMPAIGN_TERMINATE, { campaign_id, job_id });
  }

  outcallJobReschedule(campaign_id: number, job_id: number, scheduled_dt: DateTime): Promise<any> {
    const scheduledDate = scheduled_dt.toFormat('yyyy-MM-dd');
    const scheduledTime = scheduled_dt.toFormat('HH:mm');
    return HttpEngine.getInstance()
      .apiPostV2(this.URL_OUTCALL_CAMPAIGN_RESCHEDULE, { campaign_id, job_id, scheduled_date: scheduledDate, scheduled_time: scheduledTime })
      .then(
        (response: any) => {
          this.loggerService.log('outcall_job_reschedule response', response);

          return response;
        },
        (response: any) => {
          this.loggerService.error('outcall_job_reschedule response', response);

          return response;
        }
      );
  }

  get_outcall_campaign_item_detail(campaign_item_id): Promise<any> {
    const params = {
      campaign_item_id
    };

    return HttpEngine.getInstance().apiGetV2(this.URL_OUTCALL_CAMPAIGN_ITEM_DETAIL, params);
  }

  connect_callcenter(operator_id, inout_state = 0): Promise<any> {
    this.loggerService.log('connect_callcenter', operator_id);

    return HttpEngine.getInstance().apiGetV2(this.URL_CONNECT_CALLCENTER, { operator_id, inout_state });
  }

  private _send_session_command(command, session_id, params?): Promise<string> {
    const data = { command, session_id, params };

    return this.wsRpcService.call_remote('send_session_command', data);
  }

  getMyCallcenterStatus(): MyCallCenterStatus {
    return MyCallCenterStatusRepository.getInstance<MyCallCenterStatusRepository>().getMyStatus();
  }
}
